import { hasPermission } from "@/lib/permissions";
import { billingAccessCopy } from "@/lib/billingAccessCopy";
import React, { useEffect, useState } from "react";
import { Check, Coins, Loader2, LockKeyhole, ShieldCheck, Sparkles, WalletCards } from "lucide-react";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { useAuth, useWorkspace } from "@/lib/store";
import { useLocalization } from "@/lib/localization";
import { confirmCreditCheckout, fetchBillingWallet, fetchCreditPacks, initializeCreditCheckout } from "@/lib/shopApi";
import { toast } from "sonner";
import { parityCopy } from "@/lib/parityLocalization";
import { normalizeDollarSymbol } from "@/lib/money";
import { checkoutCopy, creditPaymentCopy } from "@/lib/commerceLocalization";

const pendingCreditKey = (shopId) => `shopbook.credits.pending-reference.${shopId || "unknown"}`;
const pendingCreditUrlKey = (shopId) => `${pendingCreditKey(shopId)}.url`;

function money(minor, currency, locale) {
  return normalizeDollarSymbol(new Intl.NumberFormat(locale, { style: "currency", currency, currencyDisplay: "narrowSymbol", maximumFractionDigits: currency === "NGN" ? 0 : 2 }).format((Number(minor) || 0) / 100));
}

export default function Credits() {
  const { session } = useAuth();
  const { credits, refreshCredits, setCreditBalance } = useWorkspace();
  const { language, locale } = useLocalization();
  const copy = parityCopy[language].credits;
  const paymentCopy = creditPaymentCopy[language] || creditPaymentCopy.en;
  const checkout = checkoutCopy[language] || checkoutCopy.en;
  const [packs, setPacks] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [supportsSquad, setSupportsSquad] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busyPack, setBusyPack] = useState("");
  const [confirming, setConfirming] = useState(false);
	const [pendingReference, setPendingReference] = useState(() => {
	  try { return window.sessionStorage.getItem(pendingCreditKey(session?.shopId)) || ""; } catch { return ""; }
	});
	const [pendingUrl, setPendingUrl] = useState(() => {
	  try { return window.sessionStorage.getItem(pendingCreditUrlKey(session?.shopId)) || ""; } catch { return ""; }
	});
	const [confirmAttempt, setConfirmAttempt] = useState(0);
  const canManageBilling = hasPermission(session, "billing");

  useEffect(() => {
    if (!session?.sessionId) return;
    void Promise.all([fetchCreditPacks(session.sessionId), fetchBillingWallet(session.sessionId)]).then(([response, walletResponse]) => {
      setPacks(response?.packs ?? []);
      setSupportsSquad(response?.availableProviders?.includes("squad"));
      setWallet(walletResponse?.wallet || null);
    }).catch(() => toast.error(paymentCopy.loadError)).finally(() => setLoading(false));
  }, [paymentCopy.loadError, session?.sessionId]);

  useEffect(() => {
    if (!session?.sessionId || typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
	const reference = params.get("transaction_ref") || params.get("reference") || params.get("trxref") || pendingReference;
    if (!reference) return;
    setConfirming(true);
    void confirmCreditCheckout(session.sessionId, reference)
      .then(async (response) => {
        if (response?.credits) setCreditBalance(response.credits);
		const nextWallet = await fetchBillingWallet(session.sessionId).catch(() => null);
		if (nextWallet?.wallet) setWallet(nextWallet.wallet);
		setPendingReference("");
		setPendingUrl("");
		try { window.sessionStorage.removeItem(pendingCreditKey(session?.shopId)); window.sessionStorage.removeItem(pendingCreditUrlKey(session?.shopId)); } catch {}
        toast.success(copy.confirmed);
        window.history.replaceState({}, "", window.location.pathname);
      })
      .catch((error) => toast.error(error?.message || copy.pending))
      .finally(() => setConfirming(false));
	}, [confirmAttempt, copy.confirmed, copy.pending, pendingReference, session?.sessionId, session?.shopId, setCreditBalance]);

  async function buy(packId) {
    setBusyPack(packId);
    try {
      const returnUrl = `${window.location.origin}/credits`;
      const response = await initializeCreditCheckout(session.sessionId, { packId, returnUrl });
	  if (response?.checkout?.settled) {
		const confirmed = await confirmCreditCheckout(session.sessionId, response.checkout.reference);
		if (confirmed?.credits) setCreditBalance(confirmed.credits);
		const nextWallet = await fetchBillingWallet(session.sessionId).catch(() => null);
		if (nextWallet?.wallet) setWallet(nextWallet.wallet);
		toast.success(copy.confirmed);
		setBusyPack("");
		return;
	  }
	  if (!response?.checkout?.checkoutUrl) throw new Error("Checkout did not return a payment link");
	  setPendingReference(response.checkout.reference);
	  setPendingUrl(response.checkout.checkoutUrl);
	  try { window.sessionStorage.setItem(pendingCreditKey(session?.shopId), response.checkout.reference); window.sessionStorage.setItem(pendingCreditUrlKey(session?.shopId), response.checkout.checkoutUrl); } catch {}
      window.location.assign(response.checkout.checkoutUrl);
    } catch (error) {
      toast.error(paymentCopy.checkoutError);
      setBusyPack("");
    }
  }

  return (
    <PageContainer>
      <div className="mx-auto max-w-5xl">
        <header className="max-w-2xl">
          <div className="flex items-center gap-2 text-sm font-semibold text-primary"><Coins className="h-4 w-4" /> {copy.eyebrow}</div>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight">{copy.title}</h1>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">{copy.subtitle}</p>
        </header>

        <section className="mt-7 overflow-hidden rounded-2xl border border-primary/15 bg-primary/[0.045] p-5 sm:flex sm:items-end sm:justify-between">
          <div><div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{copy.available}</div><div className="mt-2 text-4xl font-semibold tracking-tight text-primary">{credits?.availableCredits ?? "0"}</div><div className="mt-1 text-xs text-muted-foreground">{copy.credits}</div></div>
          <div className="mt-4 flex gap-6 text-sm sm:mt-0"><Balance label={copy.reserved} value={credits?.reservedCredits ?? "0"} /><Balance label={copy.total} value={credits?.balanceCredits ?? "0"} /></div>
        </section>

		{confirming ? <div className="mt-5 flex items-center gap-2 rounded-xl border bg-card p-4 text-sm"><Loader2 className="h-4 w-4 animate-spin text-primary" /> {copy.confirming}</div> : pendingReference ? <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-950"><div className="text-sm font-semibold">{checkout.interrupted}</div><p className="mt-1 text-xs text-amber-900/70">{checkout.interruptedBody}</p><div className="mt-3 flex gap-2"><Button size="sm" variant="outline" onClick={() => setConfirmAttempt((value) => value + 1)}>{checkout.verify}</Button>{pendingUrl ? <Button size="sm" onClick={() => window.location.assign(pendingUrl)}>{checkout.resume}</Button> : null}</div></div> : null}

        <div className="mt-5 flex items-start gap-3 rounded-2xl border border-primary/15 bg-primary/[0.045] p-4"><WalletCards className="mt-0.5 h-5 w-5 text-primary" /><div><div className="text-sm font-semibold">{paymentCopy.walletFirst}</div><p className="mt-1 text-xs leading-5 text-muted-foreground">{paymentCopy.walletRoute}</p></div></div>

        <div className="mt-8 flex items-center gap-3"><h2 className="text-sm font-semibold">{copy.topUp}</h2><div className="h-px flex-1 bg-border" /></div>
        {!canManageBilling ? (
          <div className="mt-4 flex items-start gap-3 rounded-2xl border bg-card p-5"><LockKeyhole className="mt-0.5 h-4 w-4 text-muted-foreground" /><div><div className="text-sm font-semibold">{copy.ownerRequired}</div><p className="mt-1 text-xs text-muted-foreground">{billingAccessCopy[language].message}</p></div></div>
        ) : loading ? (
          <div className="mt-4 py-14 text-center text-sm text-muted-foreground">{copy.loading}</div>
        ) : (
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            {packs.map((pack) => {
              const walletCovers = wallet?.currencyCode === pack.currencyCode && Number(wallet.availableMinor) >= Number(pack.priceMinor);
              const unavailable = !walletCovers && !supportsSquad;
              return (
              <article key={pack.id} className={`relative flex min-h-56 flex-col rounded-2xl border bg-card p-5 ${pack.popular ? "border-primary/35 shadow-[0_18px_50px_-36px_hsl(var(--primary))]" : ""}`}>
                {pack.popular ? <div className="absolute right-4 top-4 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-primary"><Sparkles className="h-3 w-3" /> {copy.popular}</div> : null}
                <div className="text-sm font-semibold">{pack.name}</div>
                <div className="mt-5 text-3xl font-semibold tracking-tight">{Number(pack.credits).toLocaleString(locale)}</div>
                <div className="mt-1 text-xs text-muted-foreground">{copy.credits}</div>
                <div className="mt-4 flex items-start gap-2 rounded-xl bg-muted/55 p-3 text-[11px] leading-4 text-muted-foreground">{walletCovers ? <WalletCards className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" /> : <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />}<span>{walletCovers ? paymentCopy.walletCovers(money(wallet.availableMinor, wallet.currencyCode, locale)) : unavailable ? paymentCopy.unavailableHint : paymentCopy.walletRoute}</span></div>
                <div className="mt-auto flex items-end justify-between gap-3 pt-6"><div className="text-sm font-semibold">{money(pack.priceMinor, pack.currencyCode, locale)}</div><Button size="sm" variant={pack.popular ? "default" : "outline"} disabled={Boolean(busyPack) || unavailable} onClick={() => buy(pack.id)}>{busyPack === pack.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{walletCovers ? paymentCopy.walletInstant : copy.choose} <Check className="ml-1.5 h-3.5 w-3.5" /></>}</Button></div>
              </article>
            ); })}
          </div>
        )}

        <div className="mt-6 text-center"><button className="text-xs text-muted-foreground underline-offset-4 hover:underline" onClick={() => void refreshCredits()}>{copy.refresh}</button></div>
      </div>
    </PageContainer>
  );
}

function Balance({ label, value }) { return <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div><div className="mt-1 font-mono text-base font-semibold tabular-nums">{value}</div></div>; }
